/**
 * THE ENTITY CHAT'S CONVERSATION — everything the chat decides between a send and what the page shows, as
 * plain TypeScript (ADR 0008: the component calls this and renders what it returns; it never decides).
 * It reaches the page ONLY through `EntityChatPage` and the model ONLY through `post` (the live studioChat
 * proxy, Q33). It never saves on its own (ADR 0010): only `saveNow()` and a switch's Save call the page.
 * ⚠ `import type` only for entity.model — see rules/entity-form-rules.ts.
 */
import type { Entity } from '../../../../model/entity.model';
import type { Crud, EntityEdit, EntityParse, OotbInfo, PermissionChange, RoleInfo } from '../zs-entity.types';
import type { FixReport } from '../../sync-entities.types';
import { callEntityParse, type EntityPost } from '../parse/entity-parse';
import { validateEntityParse } from '../parse/entity-validate';
import { buildContextBlock } from '../parse/entity-prompt';
import { buildEntitySummary, entitiesWithOpen, renderEntitySummary, renderOotb, renderRoles } from '../summary/entity-summary';
import { ENTITY_CHANGED_TEXT, FAILED_TEXT, FIELD_FORM_OPEN_TEXT, openKey, routeEntityParse } from '../route/entity-route';
import { CLOSE_FIRST_TEXT, PERMISSIONS_OPEN_TEXT, type HoldStep } from '../route/action-route';
import { countUnsavedChanges, REMINDER_FIRST, reminderDue, saveActionFor } from '../apply/unsaved-count';
import { canUndo, undoPoint, undoTo, type UndoPoint } from '../apply/undo';
import { abandonedTurnRecord, entityTurnRecord, heldOutcomeRecord, type EntityTurnRecord } from '../entity-turn-record';
import { isFinalized, sameEntityName } from '../rules/entity-form-rules';
import {
  ACTION_FAILED_TEXT, applyReplyText, closedDropText, continueText, declineReplyText, droppedText, heldText,
  importReplyText, localDropText, notDoneText, notOpenText, permissionsDoneText, PERMISSIONS_FAILED_TEXT,
  reminderText, resumeText, splitLinked, switchQuestion, syncReplyText, waitingNames, type ApplyStep,
} from './reply';

/** Everything the chat may read from or ask of the entity page. The page implements it (plan 3b T5). */
export interface EntityChatPage {
  clientId(): string;
  /** Every entity the page loaded, hidden included. */
  entities(): readonly Entity[];
  /** The open entity's unsaved edits, or null when none is open. */
  open(): Entity | null;
  /** The open entity as last saved; null when it was never saved. */
  openSaved(): Entity | null;
  /** The Add/Edit Field form is open. */
  fieldFormOpen(): boolean;
  /** Make `entity` the open entity's unsaved edits, AS-IS, through the page's own open path. */
  adopt(entity: Entity, opened: 'new' | 'existing' | 'open'): void;
  /** Undo: these unsaved edits become the open entity's again; null closes the entity. */
  restore(entity: Entity | null): void;
  /** Close the open entity without saving. */
  discard(): void;
  /** The page's own Save or Update. False when the page refused (e.g. a name problem). */
  save(): Promise<boolean>;
  /** Open the page's field form on a field of the open entity. */
  editField(path: string[], field: string): void;
  /** Call `onChange` whenever the page's entities or its open entity change; returns an unsubscribe. */
  watch(onChange: () => void): () => void;
  mint(): string;
  now(): Date;
  /** Plan 4b — the roles, with what each may do per entity id. */
  roles(): readonly RoleInfo[];
  /** Plan 4b — the standard entities the page's Import Dependencies cards offer. */
  ootb(): readonly OotbInfo[];
  /** Plan 4b — the Role Permissions (or Add Role) modal is open. */
  permissionsOpen(): boolean;
  /** Plan 6 — the page's Predefined rows window is open. */
  sampleRowsOpen(): boolean;
  /** Plan 4b — import these standard entities, in this order, through the page's own import. */
  runImport(ids: string[]): Promise<{ done: string[]; error: string | null }>;
  /** Plan 4b — the page's own Sync. */
  runSync(): Promise<{ report: FixReport | null; error: string | null }>;
  // Was (2026-10-03, plan 4b Task 5 fix round 1 — carried the chat's possibly stale `next` row instead of
  // the approved CHANGE, which the page could apply over a row it reloads at approval):
  // /** Plan 4b — set (null: remove) one role's permissions on the OPEN entity through the page's Role
  //  * Permissions path, and save them. False when the page did not save. */
  // runPermissions(entityId: string, roleId: string, next: Crud | null): Promise<boolean>;
  /** Plan 4b — apply the approved permission change to one role's row on the OPEN entity, as the page loads it
   * at approval, through the page's Role Permissions path, and save. `now` = the row as saved (null = removed). */
  runPermissions(entityId: string, roleId: string, change: PermissionChange): Promise<{ saved: boolean; now: Crud | null }>;
  /** Plan 4b final review (I1) — re-read the entity list; the page's watch fires when it lands. */
  refreshEntities(): void;
}

export interface ChatEntry {
  role: 'user' | 'assistant'; text: string; requestId: string;
  /** Plan 6 (Q50) — a line from the app itself (Saved., Undone., Not done…), shown quietly in the shared history. */
  notice?: boolean;
  /** Nav 8b — a copy of the navigator's own line (shown here; the tab's history already has it). */
  fromNav?: boolean;
}
export interface ChatOption { id: string; label: string }
/** What the reply card shows. `save` = the reminder's button, named as the page names it. */
export interface ChatCard {
  question: string | null; text: string; options: ChatOption[]; undo: boolean; save: 'Save' | 'Update' | null;
  /** Plan 4b final review (M1) — a held action's card: Undo never sits on it. */
  held?: boolean;
}

export const STOPPED_TEXT = 'Stopped — nothing changed.';
export const SAVE_FAILED_TEXT = 'The page didn\'t save — fix what it says, then ask again.';
export const CANCELLED_TEXT = 'Okay — nothing changed.';
export const SAVED_TEXT = 'Saved.';
export const UNDONE_TEXT = 'Undone.';
export const NOTHING_TO_SAVE_TEXT = 'Nothing is open to save.';
export const SEND_AGAIN_LABEL = 'Send again';

type Held = { kind: 'switch' | 'ask'; parse: EntityParse; requestId: string };
/** One turn of the model's own back-and-forth within a single request (ADR 0001). */
type ChatTurn = { role: 'user' | 'assistant'; content: string };

const clone = <T>(x: T): T => JSON.parse(JSON.stringify(x)) as T;
/** Case-insensitive, null-safe name compare (fix round 1, FIX 1/FIX 2). */
// Unused since plan 4a final review (K1) — sameEntityName replaces it.
const same = (a: string | null, b: string): boolean => !!a && a.toLowerCase() === b.toLowerCase();

export class EntityChatSession {
  readonly messages: ChatEntry[] = [];
  card: ChatCard | null = null;
  busy = false;
  /** Nav 8a (Q18b) — the kind of the last routed step ('decline' when it declined), or null. */
  lastStepKind: string | null = null;
  /** Fix round 1 (2026-10-03, FIX C) — what a `busy` chat is doing, for the page to show. */
  // Was (2026-10-03, plan 4b — approving a held action is also something a busy chat can be doing):
  // working: 'Thinking' | 'Saving' = 'Thinking';
  working: 'Thinking' | 'Saving' | 'Working' = 'Thinking';
  // Was (2026-10-03, plan 4a — resuming on finalize needs to know which entity it is waiting on):
  // waiting: { requestId: string; edits: EntityEdit[] } | null = null;
  // Was (2026-10-03, fix round 1 — FIX 1, a name alone was lost by a rename before Finalize; `draft` is
  // `step.entity` itself, so the SAME object the page's Save mutates with an id and the cleaned name):
  // waiting: { requestId: string; edits: EntityEdit[]; after: string } | null = null;
  /** Edits for later entities of the last request (Q5). `after` = the entity's name at apply time; `draft`
   * is the live entity object the page adopted, found by id once saved (FIX 1). checkWaiting() resumes
   * them, with no model call, once the entity they link to (if any) is finalized and the editor closed. */
  waiting: { requestId: string; edits: EntityEdit[]; after: string; draft: Entity } | null = null;
  /** Plan 4b final review (I1) — the request whose lagging list was already re-read once (no loop). */
  private refreshedFor: string | null = null;

  private held: Held | null = null;
  // Was (2026-10-03, fix round 1 — FIX A, studioChat is stateless per hop: only the id was kept, so the
  // model's own question never reached the next call):
  // private askOpen: string | null = null;
  /** The model-level question this request is waiting on: the builder's choice continues it with history
   * (ADR 0001). Null for a route-level ask (held, above) and when nothing is pending. */
  // Was (2026-10-03, plan 4a — a second question lost the first turn, because only the last sentence and
  // question were kept: the whole request's history now accumulates instead):
  // private modelAsk: { requestId: string; sentence: string; question: string } | null = null;
  private modelAsk: { requestId: string; history: ChatTurn[] } | null = null;
  private undoAt: UndoPoint | null = null;
  /** Fix round 1 (2026-10-03, FIX B) — the open entity as saved at the last apply, so Undo never reaches
   * across a save made on the page (which the session does not otherwise hear about). */
  private savedAtApply: string | null = null;
  private lastRemindedAt: number | null = null;
  private abort: AbortController | null = null;
  private lastRequest = 'none';
  /** Final review (2026-10-03, J4) — the sentence Send again resends, as a fresh request. */
  private lastSentence = '';
  /** Plan 4a final review (I3) — the note the NEXT card leads with, when a new request dropped the last
   * one's waiting edits; said once, then cleared. */
  private dropNote: string | null = null;
  /** Plan 4b — the action waiting in the confirm bar for the builder's approval (ADR 0010). */
  private pending: { step: HoldStep; requestId: string } | null = null;

  constructor(
    private readonly page: EntityChatPage,
    private readonly post: EntityPost,
    private readonly record: (clientId: string, rec: EntityTurnRecord) => void,
    readonly conversationId: string,
    private readonly newRequestId: () => string,
  ) {}

  /** Recomputed on every read, so changes made through the form count too (spec section 4). */
  unsavedCount(): number {
    const open = this.page.open();
    return open ? countUnsavedChanges(this.page.openSaved(), open) : 0;
  }

  /** Plan 4b — what the confirm bar shows, or null when nothing is held. */
  get pendingAction(): { label: string; actionWord: string; tone: 'red' | 'blue' } | null {
    const p = this.pending?.step;
    return p ? { label: p.label, actionWord: p.actionWord, tone: p.tone } : null;
  }

  canUndo(): boolean {
    if (this.pending) return false; // plan 4b — Undo never sits beside a held action, and never undoes one
    // Fix round 1 (2026-10-03, FIX B) — Undo is only ever the chat's own work: a save made on the page
    // (which does not call back into the session) must drop it, even though `undoAt` itself looks live.
    return canUndo(this.undoAt, this.page.open()) && JSON.stringify(this.page.openSaved()) === this.savedAtApply;
  }

  async send(sentence: string, selectedField: string[] | null = null): Promise<void> {
    // Nav 8a fix round 1 — reset BEFORE the guard: an early return here (busy — e.g. a card-button choose()
    // running while the navigator was asked) must not leave a previous turn's 'decline' in place, or
    // settle('entity', true) raises an offer for a sentence this send() never ran.
    this.lastStepKind = null;
    const text = sentence.trim();
    if (!text || this.busy) return;
    // Final review (2026-10-03, J4) — remembered so Send again can resend it as a fresh request.
    this.lastSentence = text;
    // Was (2026-10-03, fix round 1 — FIX A, only the request id was kept, so the model's own question
    // and the sentence that produced it never reached the next call):
    // const requestId = this.askOpen ?? this.newRequestId();
    // this.askOpen = null;
    const continuing = this.modelAsk;
    const requestId = continuing?.requestId ?? this.newRequestId();
    // Was (2026-10-03, plan 4a — the whole request's history now lives on modelAsk itself):
    // const history = continuing
    //   ? [{ role: 'user' as const, content: continuing.sentence }, { role: 'assistant' as const, content: continuing.question }]
    //   : undefined;
    const history = continuing?.history;
    this.modelAsk = null;
    this.held = null;
    // Was (2026-10-03, plan 4a — a drop is now on the record, not silent):
    // this.waiting = null; // a new request abandons the last one's waiting edits (Q16)
    // Was (2026-10-03, plan 4a final review, I3 — the drop is now also said on the next card):
    // this.dropWaiting(); // a new request abandons the last one's waiting edits, on the record (Q16)
    const droppedNames = this.dropWaiting(); // a new request abandons the last one's waiting edits, on the record (Q16)
    // Was (2026-10-03, plan 4a final review, I3 — said on the next card, not silent):
    // this.dropNote = droppedNames.length ? droppedText(droppedNames) : null;
    // Plan 4b — a held action that was never approved goes too, on the record, and is named.
    const droppedAction = this.dropPending();
    const notes = [droppedNames.length ? droppedText(droppedNames) : null, droppedAction ? notDoneText(droppedAction) : null]
      .filter((x): x is string => !!x);
    this.dropNote = notes.length ? notes.join('\n') : null;
    this.lastRequest = requestId;
    this.messages.push({ role: 'user', text, requestId });
    this.card = null;
    this.busy = true;
    this.working = 'Thinking';
    const abort = new AbortController();
    this.abort = abort;
    // Fix round 1 (2026-10-03, FIX C) — a throw anywhere below (the page included) can never leave the
    // chat stuck busy.
    try {
      const open = this.page.open();
      const keyAtSend = openKey(open);
      const context = buildContextBlock({
        // Was (2026-10-03, final review — J3, the model never saw the open entity's unsaved edits):
        // summary: renderEntitySummary(buildEntitySummary(this.page.entities())),
        summary: renderEntitySummary(buildEntitySummary(entitiesWithOpen(this.page.entities(), open))),
        roles: renderRoles(this.page.roles(), entitiesWithOpen(this.page.entities(), open)),
        ootb: renderOotb(this.page.ootb()),
        open: open ? { name: open.name, status: open.status } : null,
        selectedField,
      });
      const raw = await callEntityParse({
        sentence: text, context, post: this.post, clientId: this.page.clientId(),
        conversationId: this.conversationId, signal: abort.signal, history,
      });
      if (abort.signal.aborted) { this.say(STOPPED_TEXT, requestId, true); return; }
      this.finish(validateEntityParse(raw), requestId, keyAtSend, text, history ?? []);
    } catch {
      // Final review (2026-10-03, J5) — a throw from the page (or anything above) says so on the card,
      // instead of leaving the chat stuck or the error unhandled.
      this.say(FAILED_TEXT, requestId, true);
    } finally {
      this.busy = false;
      this.abort = null;
    }
  }

  /** Stop the in-flight call. Nothing applies (spec section 4). */
  cancel(): void {
    this.abort?.abort();
  }

  /** The page is going away: stop any call and drop waiting edits on the record (plan 4a final review). */
  dispose(): void {
    this.cancel();
    this.dropWaiting();
    this.dropPending();
  }

  /** An option on the card: a switch's Save / Discard / Cancel, a route ask's entity, or a model ask's answer. */
  async choose(id: string): Promise<void> {
    // Final review (2026-10-03, J1) — a held switch's options must survive a click made while the field
    // form is open: say() would replace the card and lose them, so only the message is pushed.
    if (this.page.fieldFormOpen()) {
      // Was (2026-10-04, plan 6 — no `notice` key, though this is the app's own line):
      // this.messages.push({ role: 'assistant', text: FIELD_FORM_OPEN_TEXT, requestId: this.lastRequest });
      this.messages.push({ role: 'assistant', text: FIELD_FORM_OPEN_TEXT, requestId: this.lastRequest, notice: true });
      return;
    }
    // Fix round 1 (2026-10-03, FIX C) — nothing starts while a save (or a send) is already running.
    if (this.busy) return;
    // Final review (2026-10-03, J4) — Send again resends the last sentence as a fresh request.
    if (id === 'resend') { await this.send(this.lastSentence); return; }
    const held = this.held;
    const label = this.card?.options.find((o) => o.id === id)?.label ?? id;
    if (held) {
      this.held = null;
      this.messages.push({ role: 'user', text: label, requestId: held.requestId });
      if (held.kind === 'switch') {
        if (id === 'cancel') { this.say(CANCELLED_TEXT, held.requestId, true); return; }
        if (id === 'save') {
          this.busy = true;
          this.working = 'Saving';
          let ok: boolean;
          try {
            ok = await this.page.save();
          } finally {
            this.busy = false;
          }
          if (!ok) { this.say(SAVE_FAILED_TEXT, held.requestId, true); return; }
          this.lastRemindedAt = null;
          // Fix round 1 (2026-10-03, FIX B) — as saveNow() does: a save made here closes the entity too.
          this.undoAt = null;
        }
        if (id === 'discard') this.page.discard();
        // The SAME parse, re-routed against the page as it is now — no second model call (3a-R7).
        this.finish(held.parse, held.requestId, openKey(this.page.open()), '');
        return;
      }
      // A route-level ask: the choice names the entity the sentence left out (3a-R8).
      const filled: EntityParse = {
        ...held.parse, edits: held.parse.edits.map((e) => (e.entity === null ? { ...e, entity: id } : e)),
        // Plan 4b final review (M2) — a permission request's missing entity is the pick too.
        action: held.parse.action && held.parse.action.entity === null ? { ...held.parse.action, entity: id } : held.parse.action,
      };
      this.finish(filled, held.requestId, openKey(this.page.open()), '');
      return;
    }
    // A question the model asked: the choice is the next sentence of the same request.
    await this.send(label);
  }

  undo(): void {
    // Final review (2026-10-03, J1) — nothing acts while the field form is open; held/undoAt/modelAsk/
    // waiting are left untouched so the builder can answer again once the form is closed.
    if (this.page.fieldFormOpen()) { this.say(FIELD_FORM_OPEN_TEXT, this.lastRequest, true); return; }
    if (!this.undoAt || !this.canUndo()) return;
    const point = this.undoAt;
    this.undoAt = null;
    // Was (2026-10-03, plan 4a — a drop is now on the record, not silent; plan 4a final review moved this
    // BEFORE restore() — restore ran first; safe only because the component defers checkWaiting):
    // this.page.restore(undoTo(point));
    // this.waiting = null; // the request's waiting edits go with it (3a-R6)
    // this.dropWaiting(); // the request's waiting edits go with it, on the record (3a-R6, Q16)
    this.dropWaiting(); // the request's waiting edits go with it, on the record (3a-R6, Q16)
    this.page.restore(undoTo(point));
    this.say(UNDONE_TEXT, point.requestId, true);
  }

  /** The reminder's button: the page's own Save or Update. */
  async saveNow(): Promise<void> {
    // Final review (2026-10-03, J1) — nothing acts while the field form is open; held/undoAt/modelAsk/
    // waiting are left untouched so the builder can answer again once the form is closed.
    if (this.page.fieldFormOpen()) { this.say(FIELD_FORM_OPEN_TEXT, this.lastRequest, true); return; }
    // Fix round 1 (2026-10-03, FIX C) — nothing starts while a save (or a send) is already running.
    if (this.busy) return;
    // Fix round 1 (2026-10-03, MINOR) — don't call the page when there is nothing open to save.
    if (!this.page.open()) { this.say(NOTHING_TO_SAVE_TEXT, this.lastRequest, true); return; }
    this.busy = true;
    this.working = 'Saving';
    let ok: boolean;
    try {
      ok = await this.page.save();
    } finally {
      this.busy = false;
    }
    if (ok) { this.lastRemindedAt = null; this.undoAt = null; }
    this.say(ok ? SAVED_TEXT : SAVE_FAILED_TEXT, this.lastRequest, true);
  }

  /** Plan 4b — the confirm bar's approve: run the held action through the page, then say what it did. */
  async approve(): Promise<void> {
    // Was (2026-10-03, plan 4b Task 5 fix round 1 — F5, checked field-form-open before busy/pending, and
    // said so against `lastRequest` instead of the held request's own id):
    // if (this.page.fieldFormOpen()) { this.say(FIELD_FORM_OPEN_TEXT, this.lastRequest); return; }
    // const p = this.pending;
    // if (this.busy || !p) return;
    const p = this.pending;
    if (this.busy || !p) return;
    if (this.page.fieldFormOpen()) { this.say(FIELD_FORM_OPEN_TEXT, p.requestId, true); return; }
    const a = p.step.action;
    const open = this.page.open();
    if (a.kind !== 'permissions' && open) {
      this.say(CLOSE_FIRST_TEXT(open.name || 'the new entity', a.kind === 'sync' ? 'Sync' : 'Import'), p.requestId, true);
      return;
    }
    if (a.kind === 'permissions') {
      if (this.page.permissionsOpen()) { this.say(PERMISSIONS_OPEN_TEXT, p.requestId, true); return; }
      if (open?.id !== a.entityId) {
        this.dropPending();
        // Was (2026-10-03, plan 4b final review — M4, "A different entity is open now" was also shown when
        // the entity had simply been closed, which is untrue there):
        // this.say(ENTITY_CHANGED_TEXT, p.requestId);
        this.say(open ? ENTITY_CHANGED_TEXT : notOpenText(a.entityName), p.requestId, true);
        return;
      }
    }
    this.pending = null;
    this.messages.push({ role: 'user', text: p.step.actionWord, requestId: p.requestId });
    this.busy = true;
    this.working = 'Working';
    let text: string;
    let failed = false;
    try {
      if (a.kind === 'import') {
        const res = await this.page.runImport(a.ids);
        text = importReplyText(res, a.names);
        failed = !!res.error;
      } else if (a.kind === 'sync') {
        const res = await this.page.runSync();
        text = syncReplyText(res, this.page.entities());
        failed = !!res.error || !res.report;
      } else {
        // Was (2026-10-03, plan 4b Task 5 fix round 1 — F1, carried the chat's possibly stale `next` row
        // instead of the approved CHANGE, which could silently undo a permission granted elsewhere meanwhile):
        // const saved = await this.page.runPermissions(a.entityId, a.roleId, a.next);
        // text = saved ? permissionsDoneText(a) : PERMISSIONS_FAILED_TEXT;
        // failed = !saved;
        const res = await this.page.runPermissions(a.entityId, a.roleId, a.change);
        text = res.saved ? permissionsDoneText({ ...a, next: res.now }) : PERMISSIONS_FAILED_TEXT;
        failed = !res.saved;
      }
    } catch {
      text = ACTION_FAILED_TEXT;
      failed = true;
    } finally {
      this.busy = false;
    }
    this.record(this.page.clientId(), heldOutcomeRecord({
      requestId: p.requestId, conversationId: this.conversationId, at: this.page.now().getTime(),
      outcome: 'approved', action: a.kind, failed,
    }));
    this.lastRequest = p.requestId;
    // Was (2026-10-03, plan 4b final review — M1, Undo must never sit beside a held action's outcome card):
    // this.say(text, p.requestId);
    // Was (2026-10-04, plan 6 final review, M4 — a failed held action's outcome read as the model's own reply):
    // this.show(p.requestId, { question: null, text, options: [], undo: false, save: null, held: true });
    this.show(p.requestId, { question: null, text, options: [], undo: false, save: null, held: true }, failed);
    // 4a final review: checkWaiting returns early while busy, so a change that landed during the action
    // would otherwise never resume waiting edits.
    this.checkWaiting();
  }

  /** Plan 4b — the confirm bar's Cancel: nothing runs. */
  reject(): void {
    const p = this.pending;
    if (!p) return;
    this.pending = null;
    this.messages.push({ role: 'user', text: 'Cancel', requestId: p.requestId });
    this.record(this.page.clientId(), heldOutcomeRecord({
      requestId: p.requestId, conversationId: this.conversationId, at: this.page.now().getTime(),
      outcome: 'rejected', action: p.step.action.kind, failed: false,
    }));
    // Was (2026-10-03, plan 4b final review — M1, Undo must never sit beside a held action's outcome card):
    // this.say(notDoneText(p.step.label), p.requestId);
    // Was (2026-10-04, plan 6 — no `notice`, though "Not done: …" is the app's own line):
    // this.show(p.requestId, { question: null, text: notDoneText(p.step.label), options: [], undo: false, save: null, held: true });
    this.show(p.requestId, { question: null, text: notDoneText(p.step.label), options: [], undo: false, save: null, held: true }, true);
  }

  dismiss(): void {
    this.card = null;
  }

  // Was (2026-10-03, plan 4a final review, K5 — gained a last `resumed` parameter, passed into the record):
  // private finish(
  //   parse: EntityParse, requestId: string, keyAtSend: string | null, sentence: string,
  //   history: ChatTurn[] = [],
  // ): void {
  private finish(
    parse: EntityParse, requestId: string, keyAtSend: string | null, sentence: string,
    history: ChatTurn[] = [], resumed = false, entitiesOverride: readonly Entity[] | null = null,
  ): void {
    const step = routeEntityParse(parse, {
      // Was (2026-10-03, plan 4b live check — the page's list, which can still lag a Finalize):
      // entities: this.page.entities(),
      entities: entitiesOverride ?? this.page.entities(),
      open: this.page.open(),
      openSaved: this.page.openSaved(),
      openKeyAtSend: keyAtSend,
      fieldFormOpen: this.page.fieldFormOpen(),
      mint: () => this.page.mint(),
      nowIso: this.page.now().toISOString(),
      roles: this.page.roles(), ootb: this.page.ootb(), permissionsOpen: this.page.permissionsOpen(),
    });
    this.record(this.page.clientId(), entityTurnRecord({
      requestId, conversationId: this.conversationId, at: this.page.now().getTime(), parse, step, resumed,
    }));
    // Nav 8a (Q18b) — the chat component reads this once send() resolves, to decide whether to settle an offer.
    this.lastStepKind = step.kind;
    switch (step.kind) {
      case 'apply':
        this.apply(step, parse, requestId);
        return;
      case 'switch': {
        this.held = { kind: 'switch', parse, requestId };
        const action = saveActionFor(this.page.open() ?? { status: 'NEW' });
        this.show(requestId, {
          question: switchQuestion(step.from, step.to, step.unsaved, action), text: '',
          options: [{ id: 'save', label: action }, { id: 'discard', label: 'Discard' }, { id: 'cancel', label: 'Cancel' }],
          undo: false, save: null,
        });
        return;
      }
      case 'ask':
        // Was (2026-10-03, plan 4b final review — M2, a permission request's "which entity?" went back to
        // the model instead of being answered locally, as an edits request's already was):
        // if (parse.kind === 'edits') this.held = { kind: 'ask', parse, requestId };
        if (parse.kind === 'edits' || parse.kind === 'action') this.held = { kind: 'ask', parse, requestId };
        // Was (2026-10-03, fix round 1 — FIX A, only the id was kept):
        // else this.askOpen = requestId;
        // Was (2026-10-03, plan 4a — only the last sentence and question were kept, so a second question
        // lost the first turn of the same request's history):
        // else this.modelAsk = { requestId, sentence, question: step.question };
        // Was (2026-10-03, fix round 1 — MINOR, an unbounded history grew forever across many questions):
        // else this.modelAsk = {
        //   requestId,
        //   history: [...history, { role: 'user', content: sentence }, { role: 'assistant', content: step.question }],
        // };
        else {
          const full: ChatTurn[] = [...history, { role: 'user', content: sentence }, { role: 'assistant', content: step.question }];
          this.modelAsk = { requestId, history: full.length > 10 ? [...full.slice(0, 2), ...full.slice(-8)] : full };
        }
        this.show(requestId, {
          question: step.question, text: '', options: step.options.map((o) => ({ id: o, label: o })), undo: false, save: null,
        });
        return;
      case 'hold':
        // Plan 4b — the page does these at once, so they wait for the builder in the confirm bar (ADR 0010).
        if (step.opens) this.page.adopt(clone(step.opens), 'existing');
        this.pending = { step, requestId };
        // Plan 4b final review (M1) — a held action's card: Undo never sits on it.
        this.show(requestId, { question: null, text: heldText(step.label), options: [], undo: false, save: null, held: true });
        return;
      case 'answer':
        this.say(step.text, requestId);
        return;
      case 'decline':
        this.say(declineReplyText(step.text, step.gaps), requestId);
        return;
      case 'blocked':
      case 'failed':
        // Was (2026-10-03, final review — J4, a blocked or failed send had no way to retry it):
        // this.say(step.text, requestId);
        // Was (2026-10-04, plan 6 — no `notice`, though a blocked/failed step's text is always one of the
        // app's own lines (FAILED_TEXT, FIELD_FORM_OPEN_TEXT, ENTITY_CHANGED_TEXT), never the model's):
        // this.show(requestId, {
        //   question: null, text: step.text, options: [{ id: 'resend', label: SEND_AGAIN_LABEL }], undo: false, save: null,
        // });
        this.show(requestId, {
          question: null, text: step.text, options: [{ id: 'resend', label: SEND_AGAIN_LABEL }], undo: false, save: null,
        }, true);
        return;
    }
  }

  private apply(step: ApplyStep, parse: EntityParse, requestId: string): void {
    // Was (2026-10-03, fix round 1 — MINOR, a drop made by hand between two requests was invisible: the
    // reset read the count only AFTER this request's own edits landed, by which point it could be back
    // at or above where it last reminded (3b fix round 1 review, test 71) — this reads the live count
    // while it still reflects the drop, before this apply changes anything):
    // (no equivalent read existed here)
    const priorCount = this.unsavedCount();
    if (priorCount < REMINDER_FIRST || (this.lastRemindedAt !== null && priorCount < this.lastRemindedAt)) this.lastRemindedAt = null;
    // Undo for an edit to the open entity restores its unsaved edits; for a new or newly opened entity it
    // closes it (the route only opens another entity when the previous one had no unsaved changes).
    const before = step.opened === 'open' ? clone(this.page.open()) : null;
    this.page.adopt(step.entity, step.opened);
    // Was (2026-10-03, fix round 1 — FIX B, `step.entity` is the route's draft, not necessarily what the
    // page ends up holding once adopt() runs it through its own open path):
    // this.undoAt = undoPoint(requestId, before, step.entity);
    this.undoAt = undoPoint(requestId, before, this.page.open() ?? step.entity);
    this.savedAtApply = JSON.stringify(this.page.openSaved());
    // Was (2026-10-03, plan 4a — resuming on finalize needs to know which entity it is waiting on):
    // this.waiting = step.waiting.length ? { requestId, edits: step.waiting } : null;
    // Was (2026-10-03, fix round 1 — FIX 1, a rename before Finalize needs the live entity, not just its
    // name at apply time):
    // this.waiting = step.waiting.length ? { requestId, edits: step.waiting, after: step.entity.name } : null;
    // Was (2026-10-03, plan 4a final review, I2 — a local first entity's linked waiting edits are now
    // dropped at once here, not left to wait for a Finalize the page doesn't offer):
    // this.waiting = step.waiting.length
    //   ? { requestId, edits: step.waiting, after: step.entity.name, draft: step.entity }
    //   : null;
    // Plan 4a final review (I2) — a first entity created LOCAL: the edits that link to it are dropped now,
    // on the record; the rest still wait for its editor to close.
    const { linked, rest } = step.entity.status === 'LOCAL'
      ? splitLinked(step.waiting, step.entity.name)
      : { linked: [] as EntityEdit[], rest: step.waiting };
    if (linked.length) {
      this.record(this.page.clientId(), abandonedTurnRecord({
        requestId, conversationId: this.conversationId, at: this.page.now().getTime(), waiting: linked.length,
      }));
    }
    this.waiting = rest.length ? { requestId, edits: rest, after: step.entity.name, draft: step.entity } : null;
    let text = applyReplyText(step, parse.dropped);
    let save: 'Save' | 'Update' | null = null;
    const count = this.unsavedCount();
    // Was (2026-10-03, fix round 1 — MINOR, a count that fell below where it last reminded never
    // reminded again, because it had not also fallen below REMINDER_FIRST):
    // if (count < REMINDER_FIRST) {
    //   this.lastRemindedAt = null;
    // } else if (reminderDue(count, this.lastRemindedAt)) {
    if (reminderDue(count, this.lastRemindedAt)) {
      this.lastRemindedAt = count;
      save = saveActionFor(step.entity);
      text += `\n${reminderText(count, save)}`;
    }
    this.show(requestId, { question: null, text, options: [], undo: true, save });
  }

  // Was (2026-10-03, fix round 1 — FIX 1/FIX 2, the whole body):
  // checkWaiting(): void {
  //   const w = this.waiting;
  //   if (!w || this.busy || this.page.open()) return;
  //   const first = this.page.entities().find((e) => e.name.toLowerCase() === w.after.toLowerCase());
  //   if (!first) return;
  //   if (first.status === 'LOCAL') {
  //     this.dropWaiting();
  //     this.say(localDropText(first.name, waitingNames(w.edits)), w.requestId);
  //     return;
  //   }
  //   if (!isFinalized(first.status)) return;
  //   this.waiting = null;
  //   this.lastRequest = w.requestId;
  //   this.messages.push({ role: 'assistant', text: resumeText(first.name), requestId: w.requestId });
  //   const parse: EntityParse = { kind: 'edits', edits: w.edits, answer: null, question: null, options: [], decline: null, gaps: [], dropped: 0 };
  //   this.finish(parse, w.requestId, openKey(this.page.open()), '');
  //   if (this.card) this.card = { ...this.card, text: `${resumeText(first.name)}\n${this.card.text}`.trim() };
  // }
  /**
   * Called by the page whenever its entities or its open entity change (plan 4a). Only edits that LINK to
   * the first entity (a reference to it) wait on it being finalized (fix round 1, FIX 2) — unlinked edits
   * resume as soon as its editor is closed, saved or not. Once ready, the SAME edits are re-routed with
   * their entity names re-pointed from the first entity's name at apply time to its final one, so a rename
   * before Finalize is never lost (FIX 1) — no model call either way (Q5).
   */
  checkWaiting(): void {
    const w = this.waiting;
    if (!w || this.busy || this.page.open()) return;
    // Was (2026-10-03, plan 4a final review, I1 — `same`/`toLowerCase()` missed a model name the form's
    // own cleanup would have changed, e.g. `customer_account` vs the page's saved `customeraccount`):
    // const linked = w.edits.some((e) => same(e.reference, w.after));
    const linked = w.edits.some((e) => sameEntityName(e.reference, w.after));
    // FIX 1 — once the page's Save has set an id on the draft (the SAME object, AS-IS), find it by id so
    // a rename before Finalize still resolves; until then, by the name it had at apply time.
    // Was (2026-10-03, plan 4a final review, I1):
    // const first = w.draft.id
    //   ? this.page.entities().find((e) => e.id === w.draft.id)
    //   : this.page.entities().find((e) => e.name.toLowerCase() === w.after.toLowerCase());
    const first = w.draft.id
      ? this.page.entities().find((e) => e.id === w.draft.id)
      : this.page.entities().find((e) => sameEntityName(e.name, w.after));
    // Plan 4b live check (2026-10-03) — the page's list can lag a Finalize: its post-save reloads race and an
    // older one can land last, so the list says NEW for an entity the page just finalized. The page's own
    // saved draft (the SAME object, its id set by Save) is the truth while the list catches up.
    const lagging = !!first && !!w.draft.id && first.id === w.draft.id && !isFinalized(first.status) && isFinalized(w.draft.status);
    // Since the final review (I1) a lagging list returns above, so firstNow === first from here on.
    const firstNow = lagging ? w.draft : first;
    if (linked) {
      // Was (2026-10-03, plan 4a final review, T3 deferred — `!first` also covers the first entity's
      // editor closed WITHOUT saving; that drops the waiting edits, with the reason, instead of waiting
      // forever for a save that never happened):
      // if (!first) return; // not saved yet: keep waiting
      if (!first) {
        // Plan 4a final review — never saved (the page's Save sets the id on this same object) and its
        // editor is closed: it was closed without saving, so nothing can link to it.
        if (!w.draft.id) {
          this.dropWaiting();
          this.lastRequest = w.requestId;
          this.say(closedDropText(w.after, waitingNames(w.edits)), w.requestId, true);
        }
        return; // saved, but the list hasn't caught up yet: keep waiting
      }
      // Plan 4b final review (I1) — a list that lags the draft is never resumed from the draft alone (a Finalize
      // whose second write failed, then Back, looks the same). Ask for ONE fresh read; its emission re-enters here.
      // Plan 4d (Q47) — N22's cause is fixed (setDoc in BaseRepositoryService.create); kept as insurance: it never fires when the list is right.
      if (lagging) {
        if (this.refreshedFor !== w.requestId) { this.refreshedFor = w.requestId; this.page.refreshEntities(); }
        return;
      }
      // Was (2026-10-03, plan 4b live check — `first.status`/`first.name`, which can still be the list's
      // stale NEW while the page's own saved draft already says otherwise):
      // if (first.status === 'LOCAL') {
      //   this.dropWaiting();
      //   this.lastRequest = w.requestId;
      //   this.say(localDropText(first.name, waitingNames(w.edits)), w.requestId);
      //   return;
      // }
      // if (!isFinalized(first.status)) return; // saved but not finalized: keep waiting
      // `first` is narrowed non-null just above; `firstNow` (either `first` or `w.draft`, both Entity) is
      // non-null too, but TS does not carry that narrowing across the two variables.
      if (firstNow!.status === 'LOCAL') {
        this.dropWaiting();
        this.lastRequest = w.requestId;
        this.say(localDropText(firstNow!.name, waitingNames(w.edits)), w.requestId, true);
        return;
      }
      if (!isFinalized(firstNow!.status)) return; // saved but not finalized: keep waiting
    }
    // Either nothing waiting links to the first entity (resume now its editor is closed — FIX 2), or it
    // does and the first entity is finalized (today's rule).
    this.waiting = null;
    this.lastRequest = w.requestId;
    // Was (2026-10-03, plan 4b live check — `first?.name`):
    // const name = first?.name ?? w.after;
    const name = firstNow?.name ?? w.after;
    const text = linked ? resumeText(name) : continueText(name);
    // Was (2026-10-04, plan 6 — no `notice`, though this line is the app's own, not the model's):
    // this.messages.push({ role: 'assistant', text, requestId: w.requestId });
    this.messages.push({ role: 'assistant', text, requestId: w.requestId, notice: true });
    // Re-point from the name at apply time to the final one (FIX 1); skip when the first entity was never
    // found (closed without saving — FIX 2's unlinked case has nothing to re-point to).
    // Was (2026-10-03, plan 4a final review, I1 — both re-point lines used `same`, which could miss a
    // model name the form's cleanup would have changed):
    // const edits = first
    //   ? w.edits.map((e) => ({
    //       ...e,
    //       entity: same(e.entity, w.after) ? first.name : e.entity,
    //       reference: same(e.reference, w.after) ? first.name : e.reference,
    //     }))
    //   : w.edits;
    // Was (2026-10-03, plan 4b live check — re-pointed to `first.name`, which can still be the list's
    // stale NEW while the page's own saved draft already carries the final name):
    // const edits = first
    //   ? w.edits.map((e) => ({
    //       ...e,
    //       entity: sameEntityName(e.entity, w.after) ? first.name : e.entity,
    //       reference: sameEntityName(e.reference, w.after) ? first.name : e.reference,
    //     }))
    //   : w.edits;
    const edits = first
      // `first` truthy here narrows `first`, not `firstNow`; they agree on truthiness (see `lagging`).
      ? w.edits.map((e) => ({
          ...e,
          entity: sameEntityName(e.entity, w.after) ? firstNow!.name : e.entity,
          reference: sameEntityName(e.reference, w.after) ? firstNow!.name : e.reference,
        }))
      : w.edits;
    const parse: EntityParse = { kind: 'edits', edits, answer: null, question: null, options: [], decline: null, gaps: [], dropped: 0 };
    // Was (2026-10-03, plan 4a final review, K5 — marked `resumed`: this apply came from waiting edits,
    // with no model call):
    // this.finish(parse, w.requestId, openKey(this.page.open()), '');
    // Was (2026-10-03, plan 4b live check — no entities override: the route saw the page's own list, which
    // can still lag the Finalize that just happened):
    // this.finish(parse, w.requestId, openKey(this.page.open()), '', [], true);
    this.finish(parse, w.requestId, openKey(this.page.open()), '', [], true, lagging ? entitiesWithOpen(this.page.entities(), w.draft) : null);
    if (this.card) this.card = { ...this.card, text: `${text}\n${this.card.text}`.trim() };
  }

  // Was (2026-10-03, plan 4a final review, I3 — the drop is now also said on the next card, so the caller
  // needs the names it dropped):
  // /** Drop the waiting edits, leaving a record that the request was abandoned (Q16). */
  // private dropWaiting(): void {
  //   if (!this.waiting) return;
  //   this.record(this.page.clientId(), abandonedTurnRecord({
  //     requestId: this.waiting.requestId, conversationId: this.conversationId,
  //     at: this.page.now().getTime(), waiting: this.waiting.edits.length,
  //   }));
  //   this.waiting = null;
  // }
  /** Drop the waiting edits, leaving a record that the request was abandoned (Q16); returns the names it
   * dropped, so the caller can say so (plan 4a final review, I3). */
  private dropWaiting(): string[] {
    if (!this.waiting) return [];
    const names = waitingNames(this.waiting.edits);
    this.record(this.page.clientId(), abandonedTurnRecord({
      requestId: this.waiting.requestId, conversationId: this.conversationId,
      at: this.page.now().getTime(), waiting: this.waiting.edits.length,
    }));
    this.waiting = null;
    return names;
  }

  /** Plan 4b — drop a held action that was never approved, on the record. Its label, or null. */
  private dropPending(): string | null {
    const p = this.pending;
    if (!p) return null;
    this.pending = null;
    this.record(this.page.clientId(), heldOutcomeRecord({
      requestId: p.requestId, conversationId: this.conversationId, at: this.page.now().getTime(),
      outcome: 'abandoned', action: p.step.action.kind, failed: false,
    }));
    return p.step.label;
  }

  // Was (2026-10-04, plan 6 — no `notice` parameter: every app line looked like a model answer):
  // private say(text: string, requestId: string): void {
  //   this.show(requestId, { question: null, text, options: [], undo: false, save: null });
  // }
  // Was (2026-10-06, nav 8b Task 4 — no `fromNav` parameter: tell()'s notice had no way to mark
  // itself as a copy of the navigator's own line):
  // private say(text: string, requestId: string, notice = false): void {
  //   this.show(requestId, { question: null, text, options: [], undo: false, save: null }, notice);
  // }
  private say(text: string, requestId: string, notice = false, fromNav = false): void {
    this.show(requestId, { question: null, text, options: [], undo: false, save: null }, notice, fromNav);
  }

  /** Nav 7b (R1) — a sentence the navigator answered for this lane (e.g. "save or discard X first"): the builder's line, then a notice. */
  tell(sentence: string, text: string): void {
    const requestId = this.newRequestId();
    // Was (2026-10-06, nav 8b Task 4 — the navigator's own history already has this line; this copy
    // is shown here but must not be read back into the shared history a second time):
    // this.messages.push({ role: 'user', text: sentence, requestId });
    this.messages.push({ role: 'user', text: sentence, requestId, fromNav: true });
    this.lastRequest = requestId;
    // Was (2026-10-06, nav 8b Task 4 — the notice this pushes is the navigator's own line too):
    // this.say(text, requestId, true);
    this.say(text, requestId, true, true);
  }

  // Was (2026-10-03, plan 4a final review, I3 — the next card leads with a note when a new request dropped
  // the last one's waiting edits, instead of staying silent):
  // private show(requestId: string, card: ChatCard): void {
  //   this.messages.push({ role: 'assistant', text: card.question ?? card.text, requestId });
  //   this.card = card;
  // }
  // Was (2026-10-04, plan 6 — no `notice` parameter, and the card's own push never carried one):
  // private show(requestId: string, card: ChatCard): void {
  //   const note = this.dropNote;
  //   this.dropNote = null;
  //   if (note) this.messages.push({ role: 'assistant', text: note, requestId });
  //   this.messages.push({ role: 'assistant', text: card.question ?? card.text, requestId });
  //   this.card = note ? { ...card, text: card.text ? `${note}\n${card.text}` : note } : card;
  // }
  // Was (2026-10-06, nav 8b Task 4 — no `fromNav` parameter: the entries this pushed could never be
  // marked as a copy of the navigator's own line, which tell() needs so the shared history (which
  // already has the navigator's line) does not read them back in a second time):
  // private show(requestId: string, card: ChatCard, notice = false): void {
  //   // Plan 4a final review (I3) — the note for waiting edits a new request dropped goes first, once.
  //   const note = this.dropNote;
  //   this.dropNote = null;
  //   // Plan 6 (Q50) — a dropped-waiting note is always the app's own line, shown quietly.
  //   if (note) this.messages.push({ role: 'assistant', text: note, requestId, notice: true });
  //   this.messages.push({ role: 'assistant', text: card.question ?? card.text, requestId, ...(notice ? { notice: true } : {}) });
  //   this.card = note ? { ...card, text: card.text ? `${note}\n${card.text}` : note } : card;
  // }
  private show(requestId: string, card: ChatCard, notice = false, fromNav = false): void {
    // Plan 4a final review (I3) — the note for waiting edits a new request dropped goes first, once.
    const note = this.dropNote;
    this.dropNote = null;
    // Plan 6 (Q50) — a dropped-waiting note is always the app's own line, shown quietly.
    // Was (2026-10-06, nav 8b final review, M4 — the note is this chat's own line, never a copy of the navigator's,
    // so marking it fromNav kept it out of the shared history; only the card's own line below carries fromNav):
    // if (note) this.messages.push({ role: 'assistant', text: note, requestId, notice: true, ...(fromNav ? { fromNav: true } : {}) });
    if (note) this.messages.push({ role: 'assistant', text: note, requestId, notice: true });
    this.messages.push({
      role: 'assistant', text: card.question ?? card.text, requestId,
      ...(notice ? { notice: true } : {}), ...(fromNav ? { fromNav: true } : {}),
    });
    this.card = note ? { ...card, text: card.text ? `${note}\n${card.text}` : note } : card;
  }
}
